import { DeleteOutlined, EditOutlined, EditTwoTone, FormOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Input, Modal, Select, Switch, Table } from 'antd'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ItemResponseMediaAgencyBeanObject, ListPagination, MediaAgencyBean } from 'src/api/api'
import { StorageKey, storageReaderFactory, storageWriterFactory } from 'src/util/Storage'
import { useDebounce } from 'src/util/useDebounce'
import { toSort, useSorter } from 'src/util/useSorter'
import mediaAgencyApi from './MediaAgencyApi'
import { formatLocale } from 'src/util/Util'

const Column = Table.Column
const Option = Select.Option

const storageWriter = storageWriterFactory('mediaAgency')
const storageReader = storageReaderFactory('mediaAgency')

interface Props { }

const MediaAgencyList: React.FC<Props> = () => {

    const [data, setData] = useState<MediaAgencyBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })

    const [search, setSearch] = useState<string>()
    const debouncedSearch = useDebounce(search, 500)

    const [active, setActive] = useState<boolean>()
    const [error, setError] = useState()

    const remove = (record: MediaAgencyBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                try {
                    await mediaAgencyApi.delete(record.id as number)

                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }

                    loadData(page)
                } catch (e) {
                    setError(e.error)
                }
            }
        })
    }

    const activate = async (record: MediaAgencyBean, index) => {
        await mediaAgencyApi.activate(record.id)

        const bean = data[index]
        bean.active = !record.active

        setData([...data])
    }

    const loadData = async (page: number) => {

        const response = await mediaAgencyApi.list(pagination.defaultPageSize, page, toSort(sorter), search, active)

        const array = response.data as Array<ItemResponseMediaAgencyBeanObject>
        const mediaAgencyBeans: MediaAgencyBean[] = array.map(item => item.data as MediaAgencyBean)

        if (mediaAgencyBeans.length == 0 && page > 1) {
            loadData(page - 1)
        } else {
            const newPagination: ListPagination = response.pagination as ListPagination
            storageWriter.write(StorageKey.SearchParams, {
                page: newPagination.currentPage
            })

            setData(mediaAgencyBeans)
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        }
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData)

    useEffect(() => {
        const searchParams = storageReader.read(StorageKey.SearchParams)
        const page = searchParams.page || pagination.current

        loadData(page)
    }, [debouncedSearch, active, sorter])

    return <Card
        title="Media Agency"
        className="list-card"
        extra={<Link to={`/media-agency/create`} className="create-new-link">
            <Button type="primary" htmlType="button" icon={<PlusOutlined />}>
                New Media Agency
            </Button>
        </Link>}
    >
        {error && <Alert message={error} type="error" />}
        <div className="list-filter-group">
            <Input prefix={<SearchOutlined />} placeholder="Search by ID, Name" onChange={e => setSearch(e.target.value || undefined)} />
            <Select placeholder="Filter by Active" onChange={setActive} allowClear>
                {Object.values(['Active', 'Inactive']).map(type => <Option key={type} value={type == 'Active'}>{type}</Option>)}
            </Select>
        </div>
        <Table
            rowKey={'id'}
            size={'middle'}
            dataSource={data}
            pagination={{ ...pagination, size: 'default' }}
            onChange={handleTableChange}
        >
            <Column key="id" title="ID" dataIndex="id" sorter />
            <Column key="name" title="Name" dataIndex="name" sorter render={(text, record: MediaAgencyBean) => <Link to={`/media-agency/${record.id}/edit`}>{text}</Link>} />
            <Column
                key="totalBudget"
                title="Total Budget"
                dataIndex="totalBudget"
                render={amount => amount ? formatLocale(amount, v => '$' + v) : ''}
            />
            <Column
                key="totalCost"
                title="Total Cost"
                dataIndex="totalCost"
                render={amount => formatLocale(amount ?? 0, v => '$' + v)}
            />
            <Column
                align="center"
                key="active"
                title="Active"
                dataIndex="active"
                render={(text, record: MediaAgencyBean, index) => <Switch checked={record.active} size="small" onChange={e => activate(record, index)} />}
            />
            <Column
                key="modified"
                title="Last Modified"
                dataIndex="modified"
                sorter
            />
            <Column align="center" key="action" title="Actions" render={(text: string, record: MediaAgencyBean) =>
                <span>
                    <Link to={`/media-agency/${record.id}/edit`}>
                        <Button type="text" >
                            <FormOutlined className="actions-btn" />
                        </Button>
                    </Link>
                    <Button type="text" danger onClick={() => remove(record)}>
                        <DeleteOutlined />
                    </Button>
                </span>
            } />
        </Table>
    </Card>
}

export default MediaAgencyList


// WEBPACK FOOTER //
// ./src/component/app/mediaAgency/MediaAgencyList.tsx